define('bindingHandlers/numberPicker',[
    'lib/knockout',
    'lib/lodash4',
    'bindingHandlers/util/spinnerUtil',
    'extensions/knockoutExtenders/numeric',
    'bindingHandlers/include'
], function (ko, _, spinnerUtil) {
    "use strict";

    /**
     * Number Picker
     *
     * @description: Use for picking numbers; any validation would need to be done on the view level.
     *
     * @param {Object} [value=undefined] - An observable value used to set the number value in
     *  the picker object. By default, this is not defined but to set any other params, this
     *  must be used.
     *
     * @param {Integer} [min=0] - An integer value used to set the minimum allowed value for
     *  the picker object. By default, this is set at 0. Note: This should never be set to
     *  a negative value as the picker will not let the value go below 0.
     *
     * @param {Integer} [max=100] - An integer value used to set the maximum allowed value
     *  for the picker object. By default, this is set at 100.
     *
     * @param {Integer} [step=1] - An integer value used to set the step value applied when
     *  clicking the Up/Down buttons. By default, this is set at 1.
     *
     * @param {Integer} [roundToNearest=0] - An integer value used to round the picker value
     *  to the nearest multiple of the integer provided. By default, this is 0 and is not
     *  applied. When roundToNearest is provided, the step parameter is set to the same value
     *  to provide consistency between the different input methods.
     *
     * @param {String} [template='templates/widgets/numberPicker'] - A string value used to
     *  set a custom template for the picker object. By default, this uses the numberPicker
     *  widget template.
     *
     * @param {Boolean} [options.showClearLink=true] - A boolean to toggle the visibility of the
     *  clear link on the picker object. By default, this is true and visible.
     *
     * @param {String} [options.setClearText='numberPicker.clear'] - A json value to set the
     *  localized string on the clear link. By default, this uses numberPicker's "Clear" string.
     *
     * @param {Integer | Object} [options.setValueOnClear=0] - An integer or observable used
     *  to set the picker value when the clear link is clicked on. By default, this is set to 0.
     *
     *
     *
     * # Example 1 - Basic binding
     *
     * <div data-bind="numberPicker: numberObservable"></div>
     *
     * In this example, you only need to provide an observable with an integer value. All
     * other options use their default values.
     *
     *
     *
     * # Example 2 - Min/Max settings
     *
     * <div data-bind="
     *      numberPicker: {
     *          value: numberObservable,
     *          min: 1,
     *          max: 50
     *      }">
     * </div>
     *
     * In this example, we set the minimum value of the picker to 1 and the maximum to 50.
     * Because we're setting min/max, we must put the numberObservable in the value parameter.
     *
     *
     *
     * # Example 3 - Step setting
     *
     * <div data-bind="
     *      numberPicker: {
     *          value: numberObservable,
     *          step: 5
     *      }">
     * </div>
     *
     * In this example, we set the step value of the picker so the numberObservable will
     * increase/decrease by that step when clicking the Up/Down buttons. {Ex: 0, 5, 10, 15...}
     *
     *
     * # Example 4 - RoundToNearest setting
     *
     * <div data-bind="
     *      numberPicker: {
     *          value: numberObservable,
     *          roundToNearest: 5
     *      }">
     * </div>
     *
     * In this example, we set the roundToNearest value of the picker to 5 so any value
     * entered will be automatically rounded to the nearest multiple of 5. Clicking the
     * Up/Down buttons will cause the value to increase/decrease by 5 as well. Note: This
     * does enforce any minimum/maximum values that are set. {Ex: 4 -> 5, 11 -> 10, 73 -> 75}
     *
     *
     *
     * # Example 5 - Custom template
     *
     * <div data-bind="
     *      numberPicker: {
     *          value: numberObservable,
     *          template: 'myComponent/templates/customNumberPicker'
     *      }">
     * </div>
     *
     * In this example, we have the picker object use a custom template for the display.
     * This custom template can live in browser app's template directory or in your own
     * component's template directory.
     *
     *
     *
     * # Example 6 - Disabling "Clear" link
     *
     * <div data-bind="
     *      numberPicker: {
     *          value: numberObservable,
     *          options: {
     *              showClearLink: false
     *          }
     *      }">
     * </div>
     *
     * In this example, we set showClearLink to false so the link will not be displayed on
     * the numberPicker template.
     *
     *
     *
     * # Example 7 - Change "Clear" link value/text
     *
     * <div data-bind="
     *      numberPicker: {
     *          value: numberObservable,
     *          options: {
     *              setClearText: 'myComponent.strings.default',
     *              setValueOnClear: 50
     *          }
     *      }">
     * </div>
     *
     * In this example, we change the text on the "Clear" link to be the localized value of the
     * json string we passed in. We also provide a setValueOnClear value which will change
     * the picker value to 50 when the "Clear" link is pressed.
     */

    ko.bindingHandlers.numberPicker = {
        init: function(element, valueAccessor, allBindings, viewModel, bindingContext) {
            var self = this;
            var number_template = 'templates/widgets/numberPicker';
            var selectedNumber = null;
            var bindingData = valueAccessor();

            // create variable to hold the number picker template
            var templateData = {};
            var tempHolder = {};
            var numberUnit = "wholeNumber";
            var unitMin = {
                wholeNumber: ko.observable(0)
            };
            var unitMax = {
                wholeNumber: ko.observable(100)
            };
            var stepRoundsValue = {};
            var step = {
                wholeNumber: 1
            };
            var roundToNearest = {
                wholeNumber: 0
            };
            var showClearLink = ko.observable(true);
            var clearText = 'numberPicker.clear';
            var valueOnClear = {
                wholeNumber: 0
            };

            //set number
            if (ko.isObservable(bindingData)) {
                selectedNumber = bindingData;
            } else if (bindingData.value && ko.isObservable(bindingData.value)) {
                selectedNumber = bindingData.value;
            }

            //check if selectedNumber is null
            if (selectedNumber === null) {
                throw new Error("NumberPicker binding error. Must at least specify as follows: data-bind='numberPicker: observable'");
            }

            //if there is a template, set it
            if (bindingData.template && typeof(bindingData.template) === 'string') {
                number_template = bindingData.template;
            }

            // if there is a minimum value, set it for all units
            if (bindingData.min) {
                var min = bindingData.min;

                if (_.isInteger(min)) {
                    unitMin[numberUnit](min);
                } else if (ko.isObservable(min)) {
                    unitMin[numberUnit] = min;
                }
            }

            // if there is a maximum value, set it for all units
            if (bindingData.max) {
                var max = bindingData.max;

                if (_.isInteger(max)) {
                    unitMax[numberUnit](max);
                } else if (ko.isObservable(max)) {
                    unitMax[numberUnit] = max;
                }
            }

            // if a roundToNearest value is specified, set it and the step accordingly
            if (bindingData.roundToNearest && _.isInteger(bindingData.roundToNearest) && bindingData.roundToNearest !== 0) {
                roundToNearest[numberUnit] = bindingData.roundToNearest;
                step[numberUnit] = bindingData.roundToNearest;
            }

            // if there is a specified step value, use it
            if (bindingData.step && _.isInteger(bindingData.step)) {
                step[numberUnit] = bindingData.step;
            }

            if (bindingData.stepRoundsValue) {
                stepRoundsValue[numberUnit] = true;
            }

            // if there are any options, set them
            if (bindingData.options && _.isObject(bindingData.options)) {
                if (!_.isUndefined(bindingData.options.showClearLink) && _.isBoolean(bindingData.options.showClearLink)) {
                    showClearLink(bindingData.options.showClearLink);
                }

                if (bindingData.options.setClearText && _.isString(bindingData.options.setClearText)) {
                    clearText = bindingData.options.setClearText;
                }

                if (bindingData.options.setValueOnClear && _.isInteger(ko.unwrap(bindingData.options.setValueOnClear))) {
                    valueOnClear[numberUnit] = ko.unwrap(bindingData.options.setValueOnClear);
                }
            }

            tempHolder[numberUnit] = selectedNumber.extend({numeric: {precision: 0, min: unitMin[numberUnit], max: unitMax[numberUnit]}});

            templateData.showClearLink = showClearLink();
            templateData.clearText = clearText;

            var unitToNumber = function() {
                var numberPicked;
                _.each(tempHolder, function(unit) {
                    numberPicked = unit();
                });
                return numberPicked;
            };

            templateData.pickedNumber = ko.computed(function() {
                return(unitToNumber());
            });

            templateData.pickedNumber.subscribe(function(newValue) {
                if(selectedNumber() != newValue) {
                    selectedNumber(newValue);
                }
            });

            selectedNumber.subscribe(function(newValue) {
                //Don't allow negative numbers
                if(newValue < 0) {
                    if (unitMin[numberUnit]() > 0) {
                        selectedNumber(unitMin[numberUnit]());
                    } else {
                        selectedNumber(0);
                    }

                    return;
                }

                //Round new number to the nearest multiple of the roundToNearest value
                if (roundToNearest[numberUnit] !== 0) {
                    var rounded = roundToNearest[numberUnit] * Math.round(newValue / roundToNearest[numberUnit]);

                    if (rounded < unitMin[numberUnit]()) {
                        rounded = unitMin[numberUnit]();
                    } else if (rounded > unitMax[numberUnit]()) {
                        rounded = unitMax[numberUnit]();
                    }

                    selectedNumber(rounded);
                    return;
                }
            });

            /**
             * this is here so that we are not breaking backwards compatibility, it takes all the values from tempHolder
             * and puts it into templateData
             */
            _.each(tempHolder, function(unit, key){
                templateData[key] = unit;
            });

            // merge the templateData set above with the click handlers from the spinner utility
            _.merge(templateData, spinnerUtil.handleClickDirections(tempHolder, step, valueOnClear, stepRoundsValue));

            var includeValueAccessor = ko.observable({
                name: number_template,
                data: templateData
            });

            ko.bindingHandlers.include.init.call(self, element, includeValueAccessor, allBindings, viewModel, bindingContext);
            ko.bindingHandlers.include.update.call(self, element, includeValueAccessor, allBindings, viewModel, bindingContext);
        }
    };
});

